import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Handshake, Users, Building2, Target, Heart, ArrowLeft,
  GraduationCap, Globe2, Briefcase, Mail, Network
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Modal from '../components/Modal';

export default function AdvertisePage() {
  const { theme } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    amount: '',
    message: '',
    type: ''
  });

  const supportTypes = [
    {
      icon: <Heart className="h-6 w-6 text-red-500" />,
      title: 'Donations',
      description: 'Support our mission to empower tech talent in developing nations through charitable contributions.',
      benefits: [
        'Tax-deductible contributions',
        'Impact reporting',
        'Recognition in our annual report',
        'Invitation to donor events'
      ],
      value: 'donation'
    },
    {
      icon: <Handshake className="h-6 w-6 text-blue-500" />,
      title: 'Sponsorships',
      description: 'Partner with us to support specific programs or initiatives while gaining brand visibility.',
      benefits: [
        'Program-specific impact reports',
        'Brand visibility in materials',
        'Participation in events',
        'Co-branded content opportunities'
      ],
      value: 'sponsorship'
    },
    {
      icon: <Building2 className="h-6 w-6 text-green-500" />,
      title: 'Investments',
      description: 'Join our mission as a strategic investor to help scale our impact.',
      benefits: [
        'Equity opportunities',
        'Quarterly performance reports',
        'Strategic input opportunities',
        'Priority access to talent pool'
      ],
      value: 'investment'
    }
  ];

  const impactStats = [
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: 'Talent Development',
      value: '150,000+',
      description: 'Professionals trained across developing nations'
    },
    {
      icon: <Globe2 className="h-8 w-8 text-green-500" />,
      title: 'Global Reach',
      value: '45+',
      description: 'Countries with active training programs'
    },
    {
      icon: <GraduationCap className="h-8 w-8 text-purple-500" />,
      title: 'Program Success',
      value: '73%',
      description: 'Average training cost reduction'
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    setIsModalOpen(false);
    setFormData({
      name: '',
      organization: '',
      email: '',
      amount: '',
      message: '',
      type: ''
    });
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Return to About Link */}
        <Link
          to="/about"
          className={`
            inline-flex items-center space-x-2 mb-8 group
            ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}
          `}
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span>Return to About Us</span>
        </Link>

        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <img 
              src="/images/group-icon.png" 
              alt="Gocity Group Logo" 
              className="h-12 w-19"
            />
          </div>
          <h1 className={`text-4xl font-bold mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Support Our Mission
          </h1>
          <p className={`text-xl max-w-3xl mx-auto ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Join us in transforming the global technology workforce through strategic investments in developing nations
          </p>
        </div>

        {/* Impact Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {impactStats.map((stat, index) => (
            <div
              key={index}
              className={`
                p-6 rounded-lg text-center
                ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
              `}
            >
              <div className="flex justify-center mb-4">
                {stat.icon}
              </div>
              <div className={`text-3xl font-bold mb-2 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`}>
                {stat.value}
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {stat.title}
              </h3>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {stat.description}
              </p>
            </div>
          ))}
        </div>

        {/* Support Types */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {supportTypes.map((type) => (
            <div
              key={type.value}
              onClick={() => {
                setSelectedType(type.value);
                setFormData(prev => ({ ...prev, type: type.value }));
                setIsModalOpen(true);
              }}
              className={`
                p-6 rounded-lg cursor-pointer transition-all duration-200
                ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                hover:scale-105 hover:shadow-lg
              `}
            >
              <div className="flex justify-center mb-4">
                {type.icon}
              </div>
              <h3 className={`text-xl font-bold text-center mb-4 ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {type.title}
              </h3>
              <p className={`text-sm text-center mb-6 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {type.description}
              </p>
              <div className="space-y-2">
                {type.benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2"
                  >
                    <div className={`h-1.5 w-1.5 rounded-full ${
                      theme === 'dark' ? 'bg-blue-400' : 'bg-blue-500'
                    }`} />
                    <span className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
              <button
                className={`
                  w-full mt-6 px-4 py-2 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                Learn More
              </button>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className={`
          max-w-2xl mx-auto text-center p-8 rounded-lg
          ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
        `}>
          <Mail className={`h-8 w-8 mx-auto mb-4 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`} />
          <h2 className={`text-2xl font-bold mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Want to Learn More?
          </h2>
          <p className={`mb-6 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Contact our team to discuss how you can support our mission and make a lasting impact in developing nations.
          </p>
          <button
            onClick={() => {
              setSelectedType('contact');
              setIsModalOpen(true);
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Contact Our Team
          </button>
        </div>

        {/* Form Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedType === 'contact' 
            ? 'Contact Our Team'
            : `Support Through ${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}`
          }
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Your Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Organization
                </label>
                <input
                  type="text"
                  value={formData.organization}
                  onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  required
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className={`
                  w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                required
              />
            </div>

            {selectedType !== 'contact' && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  {selectedType === 'donation' ? 'Donation Amount' : 'Investment Range'}
                </label>
                <input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  placeholder={selectedType === 'donation' ? 'Enter amount' : 'Enter range'}
                />
              </div>
            )}

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                className={`
                  w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                required
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className={`
                  px-4 py-2 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Submit
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
}